// WebSocket send helpers + worklet port message contracts for Deepgram PCM.

import { PCM_BUFFERED_AMOUNT_CAP, type ProcessPcmFrameResult } from "./deepgramAudio";

export type PcmCapturePortMessage = {
  type: "pcm";
  samples: Float32Array;
};

export type PcmResamplePortMessage = {
  type: "pcm16";
  pcm: ArrayBuffer;
  byteLength: number;
};

export type PcmResampleConfigMessage = {
  type: "config";
  targetSampleRateHz: number;
};

export type DeepgramAudioPath = "resample-worklet" | "main-thread";

type PcmWebSocket = Pick<WebSocket, "readyState" | "send" | "bufferedAmount">;

export function isPcmCapturePortMessage(data: unknown): data is PcmCapturePortMessage {
  if (!data || typeof data !== "object") return false;
  const msg = data as PcmCapturePortMessage;
  return msg.type === "pcm" && msg.samples instanceof Float32Array;
}

export function isPcmResamplePortMessage(data: unknown): data is PcmResamplePortMessage {
  if (!data || typeof data !== "object") return false;
  const msg = data as PcmResamplePortMessage;
  return msg.type === "pcm16" && msg.pcm instanceof ArrayBuffer && typeof msg.byteLength === "number";
}

/** Thin main-thread send for Int16 buffers produced by pcmResample.worklet.js. */
export function sendInt16PcmBuffer(
  ws: PcmWebSocket,
  pcm: ArrayBuffer,
  byteLength: number,
  bufferedAmountCap = PCM_BUFFERED_AMOUNT_CAP,
): ProcessPcmFrameResult {
  if (ws.readyState !== WebSocket.OPEN) return { status: "skipped", reason: "closed" };
  if (!pcm || byteLength <= 0) return { status: "skipped", reason: "empty" };
  if (ws.bufferedAmount > bufferedAmountCap) {
    return { status: "dropped", reason: "backpressure" };
  }
  try {
    ws.send(pcm);
    return { status: "sent", bytesSent: byteLength };
  } catch {
    return { status: "skipped", reason: "error" };
  }
}
